import { useCallback, useEffect, useState } from "react";
import { Trophy, Shuffle, Play, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Flag } from "../components/Flag";
import { fetchBracketSeed, simulateNow, fetchTeams } from "../lib/api";

const ROUND_NAMES = ["Round of 16", "Quarter-Finals", "Semi-Finals", "Final"];

export default function Bracket() {
  const [seeds, setSeeds] = useState(null); // [{seed, team}]
  const [rounds, setRounds] = useState([]); // rounds[i] = [{a,b,result?}]
  const [simulating, setSimulating] = useState(false);
  const [progress, setProgress] = useState({ round: -1, match: -1 });

  const buildR0 = (codes, seedList) =>
    [0, 2, 4, 6, 8, 10, 12, 14].map((i) => ({
      a: { ...seedList.find((s) => s.team.code === codes[i]).team },
      b: { ...seedList.find((s) => s.team.code === codes[i + 1]).team },
      result: null,
    }));

  const loadSeed = useCallback(async () => {
    try {
      const data = await fetchBracketSeed();
      setSeeds(data.seeds);
      setRounds([buildR0(data.team_codes, data.seeds)]);
      setProgress({ round: -1, match: -1 });
    } catch (e) {
      console.error("Failed to load bracket seed:", e);
    }
  }, []);

  useEffect(() => {
    loadSeed();
  }, [loadSeed]);

  const runFullBracket = async () => {
    if (!rounds[0]) return;
    setSimulating(true);
    let currentRounds = [rounds[0].map((m) => ({ ...m, result: null }))];
    setRounds(currentRounds);

    for (let r = 0; r < 4; r += 1) {
      let round = currentRounds[r];
      for (let i = 0; i < round.length; i += 1) {
        setProgress({ round: r, match: i });
        const match = round[i];
        const result = await simulateNow({ teamA: match.a.code, teamB: match.b.code, knockout: true });
        const aWin =
          result.score.a > result.score.b ||
          (result.penalties && result.pens && result.pens.a > result.pens.b);
        const updatedMatch = {
          ...match,
          result: {
            a: result.score.a,
            b: result.score.b,
            penalties: result.penalties,
            pens: result.pens,
            winner: aWin ? match.a : match.b,
            id: result.id,
          },
        };
        round = [...round.slice(0, i), updatedMatch, ...round.slice(i + 1)];
        currentRounds = [...currentRounds.slice(0, r), round, ...currentRounds.slice(r + 1)];
        setRounds(currentRounds);
      }
      // Build next round
      if (r < 3) {
        const next = round
          .filter((_, idx) => idx % 2 === 0)
          .map((m, idx) => ({
            a: round[idx * 2].result.winner,
            b: round[idx * 2 + 1].result.winner,
            result: null,
          }));
        currentRounds = [...currentRounds, next];
        setRounds(currentRounds);
      }
    }
    setProgress({ round: -1, match: -1 });
    setSimulating(false);
  };

  const reshuffle = async () => {
    if (simulating) return;
    // Shuffle Round of 16 pairings using all teams (top 16 by MSS still, randomly paired)
    const teams = await fetchTeams();
    const top16 = teams.slice(0, 16);
    const shuffled = [...top16].sort(() => Math.random() - 0.5);
    const r0 = Array.from({ length: 8 }, (_, idx) => {
      const i = idx * 2;
      return { a: shuffled[i], b: shuffled[i + 1], result: null };
    });
    setRounds([r0]);
    setProgress({ round: -1, match: -1 });
  };

  if (!seeds || rounds.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center text-zinc-400">
        Loading bracket…
      </div>
    );
  }

  const finalMatch = rounds[3]?.[0];
  const champion =
    finalMatch?.result?.winner ||
    null;

  return (
    <div className="min-h-screen pb-16">
      {/* Header */}
      <section className="border-b border-zinc-900 bg-zinc-950">
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
            Knockout Stage · Top 16 by MSS
          </div>
          <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tighter sm:text-6xl">
            The <span className="text-volt">Bracket</span>
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm text-zinc-400">
            Simulate the entire knockout tournament from the Round of 16 to the Final. The 16
            highest-MSS qualified nations seeded by strength, paired in standard bracket order.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="run-bracket-btn"
              onClick={runFullBracket}
              disabled={simulating}
              className="rounded-none bg-volt px-6 font-display text-base font-bold uppercase tracking-widest text-black hover:bg-white disabled:opacity-60"
            >
              <Play className="mr-2 h-4 w-4" />
              {simulating ? "Running…" : "Run Full Tournament"}
            </Button>
            <Button
              data-testid="reshuffle-btn"
              onClick={reshuffle}
              disabled={simulating}
              variant="outline"
              className="rounded-none border-zinc-700 bg-transparent font-display text-sm font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-900 hover:text-white"
            >
              <Shuffle className="mr-2 h-4 w-4" /> Reshuffle
            </Button>
            <Button
              data-testid="reset-bracket-btn"
              onClick={loadSeed}
              disabled={simulating}
              variant="ghost"
              className="rounded-none font-display text-sm font-bold uppercase tracking-widest text-zinc-400 hover:bg-zinc-900 hover:text-white"
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
          </div>
        </div>
      </section>

      {/* Champion banner */}
      {champion && (
        <section className="border-b border-zinc-900 bg-volt">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-6 text-black">
            <div className="flex items-center gap-4">
              <Trophy className="h-10 w-10" />
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em]">2026 World Champions</div>
                <div className="font-display text-4xl font-black uppercase tracking-tighter">
                  {champion.name}
                </div>
              </div>
            </div>
            <Flag code={champion.flag} className="h-14 w-20 object-cover" />
          </div>
        </section>
      )}

      {/* Bracket horizontal scroll */}
      <section className="mx-auto max-w-full overflow-x-auto px-6 py-10">
        <div className="flex min-w-max gap-6" data-testid="bracket-view">
          {rounds.map((round, ri) => (
            <div
              key={ROUND_NAMES[ri] || `round-${ri}`}
              className="flex w-64 flex-col justify-around gap-3"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {ROUND_NAMES[ri]}
              </div>
              {round.map((m, mi) => {
                const isActive = progress.round === ri && progress.match === mi;
                const matchKey = `${m.a?.code || "tba"}-vs-${m.b?.code || "tba"}-r${ri}`;
                return (
                  <BracketMatch
                    key={matchKey}
                    match={m}
                    active={isActive}
                    testid={`bracket-r${ri}-m${mi}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function BracketMatch({ match, active, testid }) {
  const { a, b, result } = match;
  const aWin = result && (result.a > result.b || (result.penalties && result.pens?.a > result.pens?.b));
  const bWin = result && !aWin;
  return (
    <div
      data-testid={testid}
      className={`border bg-black ${active ? "border-volt" : "border-zinc-800"}`}
    >
      <Row team={a} score={result?.a} win={aWin} pen={result?.pens?.a} penalties={result?.penalties} />
      <div className="h-px bg-zinc-900" />
      <Row team={b} score={result?.b} win={bWin} pen={result?.pens?.b} penalties={result?.penalties} />
    </div>
  );
}

function Row({ team, score, win, pen, penalties }) {
  if (!team) {
    return (
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="font-display text-sm uppercase text-zinc-700">TBD</span>
      </div>
    );
  }
  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 ${
        win ? "bg-zinc-900/60" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <Flag code={team.flag} className="h-3.5 w-5 object-cover" />
        <span
          className={`font-display text-sm font-bold uppercase ${
            win ? "text-volt" : "text-zinc-300"
          }`}
        >
          {team.name}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        {penalties && pen !== undefined && (
          <span className="font-mono text-[10px] text-zinc-500">({pen})</span>
        )}
        <span
          className={`font-display text-base font-black tabular-nums ${
            win ? "text-volt" : "text-zinc-500"
          }`}
        >
          {score !== undefined ? score : "–"}
        </span>
      </div>
    </div>
  );
}
